import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadNikkeCatalog } from '../../nikke-catalog/api/nikkeCatalogApi.js';
import { findNikkeInCatalog } from '../../nikke-catalog/domain/nikkeCatalog.js';
import { onlineRaidMembers } from '../domain/onlineRaidMembers.js';
import { RAID_CONTROL_ATTACK_LIMIT, buildRaidControlAttackCountByMember } from '../domain/raidControlMemberAttacks.js';
import { useRaidOverview } from '../../raid-overview/hooks/useRaidOverview.js';
import {
  createRaidPracticeAdmin,
  deleteRaidPracticeAdmin,
  updateRaidPracticeAdmin,
} from '../../raid-practice/api/raidPracticeAdminApi.js';
import { deleteRaidPractice, saveRaidPractice } from '../../raid-practice/api/raidPracticeApi.js';
import { isRaidControlRepracticeRequestRef } from '../../raid-control-repractice-planner/domain/raidControlRepracticeRequestCodec.js';
import {
  createRaidControlCustomOrder,
  createRaidControlOrder,
  loadRaidControlContext,
  loadRaidControlTimeline,
  syntheticRaidResultMessage,
  loadRaidControlHistory,
  markRaidControlRead,
  requestRaidControlPush,
  resyncRaidControlConnection,
  sendRaidControlMessage,
  sendRaidControlPracticeReport,
  subscribeRaidControl,
  updateRaidControlChatHeader,
  updateRaidControlOrderStatus,
} from '../api/raidControlApi.js';
import { raidControlDeckCodes } from '../domain/raidControlDeck.js';
import { raidControlTimelineMessageVisible } from '../domain/raidControlTimeline.js';
import { buildRaidControlTetrisPreview } from '../domain/raidControlTetrisRecommendation.js';
import {
} from '../domain/raidControlTetrisShareCodec.js';

const MANAGER_ROLES = new Set(['leader', 'deputy', 'owner', 'admin']);
const PRACTICE_MANAGER_ROLES = new Set(['leader', 'deputy']);
const TIMELINE_RETRY_DELAYS = [0, 80, 180, 360];
const RESUME_REFRESH_COOLDOWN_MS = 800;
// 서버에 저장된 읽음 지점은 '접속할 때 스냅샷'에서 딱 한 번 읽힌다. 그러니 떠나는 순간만
// 정확하면 되고, 보고 있는 동안 계속 올릴 이유가 없다. 이 타이머는 탭을 하루 종일 열어 둬서
// 가려지지도 닫히지도 않는 경우를 위한 보험이다(측정: 메시지마다 97% -> 이 방식 12%).
const READ_MARK_FLUSH_MS = 10 * 60_000;
// 모의전 보고 하나에 메시지 사건과 도메인 사건이 따로 날아오고, 여러 명이 연달아 올리기도 한다.
// 그때마다 방 정보를 통째로 다시 불러오면 방에 있는 사람 수만큼 같은 조회가 겹친다. 묶어서 한 번만 한다.
const SILENT_REFRESH_DEBOUNCE_MS = 700;
// 보낸 글은 실시간 구독이 거의 곧바로 밀어 준다. 그 뒤에도 안 보일 때만 다시 불러온다.
const SEND_ECHO_GRACE_MS = 900;
// 공격 상태를 연달아 바꿀 때 목록 조회를 한 번으로 묶는 간격. 사람이 눈치채지 못할 만큼
// 짧으면서, 카드를 연속으로 누르는 간격보다는 길어야 한다.
const TIMELINE_RELOAD_DEBOUNCE_MS = 300;
const text = (value) => String(value ?? '').trim();
const wait = (delay) => new Promise((resolve) => window.setTimeout(resolve, delay));

function scheduleChatScrollToLatest() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
    document.querySelectorAll('.raid-control-message-list').forEach((node) => {
      node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
    });
  }));
}

// 화면에 들고 있는 메시지 상한. 서버 스냅샷이 160개라 그보다 많이 쌓이는 건 실시간으로 받은
// 뒤 한 번도 다시 불러오지 않은 몫뿐이다. 상한이 없으면 오래 열어 둔 방에서 목록이 계속 길어져
// 정렬과 렌더가 같이 무거워진다. 지시·실전 줄도 섞이므로 스냅샷 상한보다 넉넉히 잡는다.
const TIMELINE_MESSAGE_LIMIT = 400;

// Date.parse 는 문자열을 바로 읽는다. new Date(...) 는 비교 한 번에 객체를 두 개 만든다 -
// 목록 160개를 정렬하면 비교만 1,200번쯤 돌아서 그 자리가 그대로 비용이 된다.
function timelineTime(item) {
  const parsed = Date.parse(item?.createdAt || item?.updatedAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortTimelineItems(items, ascending) {
  return items.sort((left, right) => (ascending
    ? timelineTime(left) - timelineTime(right)
    : timelineTime(right) - timelineTime(left)));
}

function capTimelineItems(items, ascending) {
  if (items.length <= TIMELINE_MESSAGE_LIMIT) return items;
  // 오름차순이면 최신이 뒤에, 내림차순이면 앞에 있다. 양쪽 다 최신 쪽을 남긴다.
  return ascending ? items.slice(items.length - TIMELINE_MESSAGE_LIMIT) : items.slice(0, TIMELINE_MESSAGE_LIMIT);
}

function upsertTimelineItem(items = [], nextItem = null, ascending = false) {
  if (!nextItem?.id) return items;
  const next = [...items];
  const index = next.findIndex((item) => item.id === nextItem.id);
  if (index >= 0) next[index] = { ...next[index], ...nextItem };
  else next.push(nextItem);
  return capTimelineItems(sortTimelineItems(next, ascending), ascending);
}

// 재연결 스냅샷은 한 덩어리로 들어온다. 메시지마다 위를 부르면 목록을 160번 복사하고 160번
// 정렬한다 - 복사 한 번, 정렬 한 번으로 끝낸다. 이미 있는 글은 그 자리에서 덮어써서 신원을
// 지킨다(말풍선 memo 가 여기에 걸려 있다).
function applyTimelineSnapshot(items = [], messages = [], removedIds = [], ascending = true) {
  const removed = new Set((removedIds || []).map((id) => String(id)));
  const byId = new Map();
  for (const item of items) if (item?.id && !removed.has(String(item.id))) byId.set(String(item.id), item);
  let changed = removed.size > 0 && byId.size !== items.length;
  for (const message of messages) {
    const id = String(message?.id || '');
    if (!id) continue;
    const current = byId.get(id);
    if (!current) { byId.set(id, message); changed = true; continue; }
    // 내용이 그대로면 기존 객체를 그대로 둔다. 새로 펼쳐 담으면 말풍선이 전부 다시 그려진다.
    const merged = { ...current, ...message };
    if (Object.keys(merged).every((key) => merged[key] === current[key])) continue;
    byId.set(id, merged);
    changed = true;
  }
  if (!changed) return items;
  return capTimelineItems(sortTimelineItems([...byId.values()], ascending), ascending);
}

function practiceIdentity(record = {}) {
  return text(record.id) || [text(record.memberOpenid), Number(record.bossNo || 0), Number(record.slotNo || 0)].join(':');
}

function normalizePracticeMutation(raw = {}, fallback = {}) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const memberOpenid = text(source.memberOpenid ?? source.member_openid ?? fallback.memberOpenid);
  const bossNo = Number(source.bossNo ?? source.boss_no ?? fallback.bossNo ?? 0);
  const slotNo = Number(source.slotNo ?? source.slot_no ?? fallback.slotNo ?? 0);
  return {
    id: text(source.id || fallback.id || `local:${memberOpenid}:${bossNo}:${slotNo}`),
    memberOpenid,
    memberNickname: text(source.memberNickname ?? source.member_nickname ?? fallback.memberNickname),
    bossNo,
    slotNo,
    title: text(source.title ?? fallback.title),
    damageEok: Number(source.damageEok ?? source.damage_eok ?? fallback.damageEok ?? 0),
    deck: Array.isArray(source.deck) ? source.deck : Array.isArray(fallback.deck) ? fallback.deck : [],
    used: Boolean(source.used ?? fallback.used),
    note: text(source.note ?? fallback.note),
    createdAt: source.createdAt ?? source.created_at ?? fallback.createdAt ?? null,
    updatedAt: source.updatedAt ?? source.updated_at ?? fallback.updatedAt ?? null,
  };
}

function upsertPracticeRecord(items = [], nextItem = null) {
  if (!nextItem) return items;
  const next = [...items];
  const identity = practiceIdentity(nextItem);
  const index = next.findIndex((item) => practiceIdentity(item) === identity
    || (text(item.memberOpenid) === text(nextItem.memberOpenid)
      && Number(item.bossNo) === Number(nextItem.bossNo)
      && Number(item.slotNo) === Number(nextItem.slotNo)));
  if (index >= 0) next[index] = { ...next[index], ...nextItem };
  else next.push(nextItem);
  return next.sort((left, right) => text(left.memberOpenid).localeCompare(text(right.memberOpenid))
    || Number(left.bossNo) - Number(right.bossNo)
    || Number(left.slotNo) - Number(right.slotNo));
}

function resolveMessageNikkeRefs(refs = [], catalog = null) {
  return (Array.isArray(refs) ? refs : []).map((ref) => {
    const source = ref && typeof ref === 'object' && !Array.isArray(ref)
      ? ref
      : { nikkeId: ref };
    const item = findNikkeInCatalog(catalog, source);
    const nikkeId = text(item?.nikkeId || source.nikkeId || source.nikke_id || source.code || source.id);
    const name = text(item?.name || source.name || source.officialName || source.official_name || nikkeId);
    const imageUrl = text(item?.imageUrl || source.imageUrl || source.image_url);
    return { nikkeId, name, imageUrl };
  }).filter((ref) => ref.nikkeId || ref.name).slice(0, 5);
}

function repracticeRequestRefIdentity(ref = null) {
  if (!isRaidControlRepracticeRequestRef(ref)) return '';
  return text(ref?.nikkeId ?? ref?.nikke_id ?? ref?.code ?? ref?.id);
}

function hasDuplicateRepracticeRequest(messages = [], refs = []) {
  const incoming = new Set((Array.isArray(refs) ? refs : []).map(repracticeRequestRefIdentity).filter(Boolean));
  if (!incoming.size) return false;
  return (Array.isArray(messages) ? messages : []).some((message) => (Array.isArray(message?.nikkeRefs) ? message.nikkeRefs : [])
    .some((ref) => incoming.has(repracticeRequestRefIdentity(ref))));
}

export function useRaidControlRoom({ userId = '' } = {}) {
  const overview = useRaidOverview();
  const seasonNo = overview.selectedSeason?.seasonNo || null;
  const [context, setContext] = useState({ unionId: '', viewerRole: 'member', season: null, members: [], practiceRecords: [] });
  const [pendingAttackConfirmations, setPendingAttackConfirmations] = useState({});
  const [orderErrors, setOrderErrors] = useState({});
  const [history, setHistory] = useState(null);
  const [chatHeader, setChatHeader] = useState({ notice: '', pinnedMessage: null });
  const [chatHeaderWorking, setChatHeaderWorking] = useState(false);
  const [chatHeaderError, setChatHeaderError] = useState('');
  const chatHeaderBusyRef = useRef(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const historyRequestRef = useRef(0);
  const historyBusyRef = useRef(false);
  const historyScopeRef = useRef('');
  const historyRemovedRef = useRef(new Set());
  const [timeline, setTimeline] = useState({ messages: [], orders: [], lastReadAt: '' });
  const [unread, setUnread] = useState({ anchorId: '', count: 0, lastReadAt: '' });
  const [catalog, setCatalog] = useState({ items: [], byKey: new Map() });
  const [selectedMemberOpenid, setSelectedMemberOpenid] = useState('');
  const [selectedBossNo, setSelectedBossNo] = useState(1);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  // 재연결·인증 대기와 정상 연결이 구분되지 않아, 입력은 되는데 전송만 안 되는 것처럼
  // 보였다. 상시 구독이 알려 주는 상태를 그대로 화면까지 올린다.
  const [realtimeStatus, setRealtimeStatus] = useState('connecting');
  const [errorMessage, setErrorMessage] = useState('');
  const [practiceNotice, setPracticeNotice] = useState({ type: '', message: '' });
  const [subscriptionEpoch, setSubscriptionEpoch] = useState(0);
  const timelineRunRef = useRef(0);
  const orderTimelineRunRef = useRef(0);
  const orderTimelineAppliedRef = useRef(0);
  const orderRefreshTimerRef = useRef(0);
  const resultTimelineRunRef = useRef(0);
  const resultTimelineAppliedRef = useRef(0);
  const resultRefreshTimerRef = useRef(0);
  const contextRunRef = useRef(0);
  const contextLoadingRef = useRef(false);
  const unreadSeasonRef = useRef('');
  const latestMessageAtRef = useRef('');
  const lastResumeRefreshAtRef = useRef(0);
  const orderMutationRef = useRef(false);
  const readMarkRef = useRef({ pendingAt: '', sentAt: '', timer: null });
  const mappedMessagesRef = useRef({ inputs: [], byMessage: new Map() });
  const reuseOverviewRecordsRef = useRef(false);
  const silentRefreshTimerRef = useRef(0);
  const timelineReloadTimerRef = useRef(0);
  const contextRef = useRef(null);
  const silentRefreshNeedsTimelineRef = useRef(false);
  const sendEchoTimersRef = useRef(new Set());
  const timelineMessagesRef = useRef([]);

  const historyScope = [context.unionId, context.season?.id, userId].join(':');
  historyScopeRef.current = historyScope;
  useEffect(() => {
    historyRequestRef.current++;
    historyBusyRef.current = false;
    historyRemovedRef.current.clear();
    setChatHeader({ notice: '', pinnedMessage: null });
    setChatHeaderError('');
    setPendingAttackConfirmations({});
    setOrderErrors({});
    setHistory(null); setHistoryLoading(false); setHistoryError('');
    return () => { historyRequestRef.current++; };
  }, [historyScope]);
  const showLatestMessages = useCallback(() => {
    historyRequestRef.current++;
    historyBusyRef.current = false;
    setHistory(null); setHistoryLoading(false); setHistoryError('');
  }, []);
  const loadOlderMessages = useCallback(async () => {
    if (historyBusyRef.current || history?.hasMore === false) return;
    const source = history?.messages || timeline.messages;
    const cursor = history?.cursor || source.filter(message => !['attack_order', 'raid_result'].includes(message.type))
      .reduce((oldest, message) => !oldest || message.createdAt < oldest.createdAt
        || (message.createdAt === oldest.createdAt && message.id < oldest.id) ? message : oldest, null);
    if (!cursor || !context.season?.id) return;
    const request = ++historyRequestRef.current;
    historyBusyRef.current = true; setHistoryLoading(true); setHistoryError('');
    try {
      const page = await loadRaidControlHistory({ unionId: context.unionId, seasonId: context.season.id,
        beforeAt: cursor.createdAt, beforeId: cursor.id });
      if (request !== historyRequestRef.current || historyScope !== historyScopeRef.current) return;
      const unique = new Map([...page.messages, ...source].filter(message => !historyRemovedRef.current.has(message.id)).map(message => [message.id, message]));
      // A separate bounded reading window: live updates retain their original fast path.
      const messages = [...unique.values()].slice(0, TIMELINE_MESSAGE_LIMIT);
      setHistory({ messages, pageVersion: request, cursor: page.messages[0] || cursor, hasMore: page.hasMore });
    } catch (error) {
      if (request === historyRequestRef.current) setHistoryError(error?.code === 'history_unsupported'
        ? '이전 채팅 조회가 아직 준비되지 않았습니다.'
        : '이전 채팅을 불러오지 못했습니다. 다시 시도해주세요.');
    } finally {
      if (request === historyRequestRef.current) { historyBusyRef.current = false; setHistoryLoading(false); }
    }
  }, [context.unionId, context.season?.id, history, historyScope, timeline.messages]);
  const visibleTimelineMessages = history?.messages || timeline.messages;

  // 접속 명단은 관제 소켓이 준다. 유니온 전체를 세던 별도 연결은 떼어 냈다 - 그 연결은
  // 관제 방에 들어오지 않은 사람까지 세려고 모든 페이지에서 상시 붙어 있었다.
  const [roomPresence, setRoomPresence] = useState([]);

  const loadTimeline = useCallback(async (nextContext, { expectedMessageId = '', expectedOrderId = '', ordersOnly = false, resultsOnly = false } = {}) => {
    if (!nextContext.unionId || !nextContext.season?.id) return false;
    const runRef = ordersOnly ? orderTimelineRunRef : resultsOnly ? resultTimelineRunRef : timelineRunRef;
    const orderAppliedAtStart = orderTimelineAppliedRef.current;
    const resultAppliedAtStart = resultTimelineAppliedRef.current;
    const runId = runRef.current + 1;
    runRef.current = runId;
    try {
      let next = null;
      let expectedRowsReady = !expectedMessageId && !expectedOrderId;
      for (const delay of TIMELINE_RETRY_DELAYS) {
        if (delay) await wait(delay);
        if (runRef.current !== runId) return false;
        // 현황이 같은 유니온·차수를 이미 받아 뒀으면 같은 160행을 한 번 더 받지 않는다.
        // 그 경우 실전 줄은 아래 matchedTimeline 이 현황 기록으로 만든다.
        next = await loadRaidControlTimeline({ unionId: nextContext.unionId, seasonId: nextContext.season.id, userId, ordersOnly, resultsOnly,
          skipRecords: !resultsOnly && reuseOverviewRecordsRef.current });
        if (runRef.current !== runId) return false;
        const messageReady = !expectedMessageId || next.messages.some((message) => message.id === expectedMessageId);
        const orderReady = !expectedOrderId || next.orders.some((order) => order.id === expectedOrderId);
        expectedRowsReady = messageReady && orderReady;
        if (expectedRowsReady) break;
      }
      if (!next || !expectedRowsReady || runRef.current !== runId) return false;
      next = { ...next, messages: capTimelineItems(next.messages, true) };
      if (ordersOnly) {
        orderTimelineAppliedRef.current++;
        setTimeline(current => ({ ...current, orders: next.orders,
          messages: capTimelineItems(sortTimelineItems([
            ...current.messages.filter(message => message.type !== 'attack_order'), ...next.messages,
          ], true), true) }));
        return true;
      }
      if (resultsOnly) {
        resultTimelineAppliedRef.current++;
        setTimeline(current => ({ ...current,
          messages: capTimelineItems(sortTimelineItems([
            ...current.messages.filter(message => message.type !== 'raid_result'), ...next.messages,
          ], true), true) }));
        return true;
      }
      const seasonKey = `${nextContext.unionId}:${nextContext.season.id}`;
      if (unreadSeasonRef.current !== seasonKey) {
        const readAt = next.lastReadAt ? new Date(next.lastReadAt).getTime() : 0;
        const unreadMessages = next.messages.filter((message) => {
          const createdAt = new Date(message.createdAt).getTime();
          return createdAt > readAt && message.authorUserId !== userId;
        });
        setUnread({
          anchorId: unreadMessages[0]?.id || '',
          count: unreadMessages.length,
          lastReadAt: next.lastReadAt || '',
        });
        unreadSeasonRef.current = seasonKey;
      }
      latestMessageAtRef.current = next.messages[next.messages.length - 1]?.createdAt || '';
      // Preserve independently refreshed rows if the full request started earlier.
      setTimeline(current => {
        const keepOrders = orderTimelineAppliedRef.current !== orderAppliedAtStart;
        const keepResults = resultTimelineAppliedRef.current !== resultAppliedAtStart;
        if (!keepOrders && !keepResults) return next;
        const preserved = message => (keepOrders && message.type === 'attack_order')
          || (keepResults && message.type === 'raid_result');
        return { ...next, orders: keepOrders ? current.orders : next.orders,
          messages: capTimelineItems(sortTimelineItems([
            ...next.messages.filter(message => !preserved(message)),
            ...current.messages.filter(preserved),
          ], true), true) };
      });
      return true;
    } catch (error) {
      if (runRef.current === runId) setErrorMessage(error instanceof Error ? error.message : '채팅을 불러오지 못했습니다.');
      return false;
    }
  }, [userId]);

  // timeline: false 면 방 정보(멤버·모의전·보스)만 다시 읽는다. 지시·실전 목록은 자기 사건이
  // 따로 오므로, 그걸 안 건드리는 변경까지 조회 두 번을 더 할 이유가 없다. 돌고 있는 조회를
  // 끊는 표시(timelineRunRef)도 그때는 올리지 않는다 - 올려 놓고 다시 안 걸면 그냥 사라진다.
  const refresh = useCallback(async ({ silent = false, timeline = true } = {}) => {
    const runId = ++contextRunRef.current;
    if (timeline) timelineRunRef.current += 1;
    if (!seasonNo) return;
    if (!silent) { contextLoadingRef.current = true; setLoading(true); }
    try {
      const next = await loadRaidControlContext(seasonNo);
      if (contextRunRef.current !== runId) return;
      setContext(next);
      setSelectedMemberOpenid((current) => current && next.members.some((member) => member.memberOpenid === current)
        ? current
        : next.members.find((member) => member.userId === userId)?.memberOpenid || next.members[0]?.memberOpenid || '');
      if (timeline) await loadTimeline(next);
      if (contextRunRef.current !== runId) return;
      setErrorMessage('');
    } catch (error) {
      if (contextRunRef.current === runId) setErrorMessage(error instanceof Error ? error.message : '레이드 지휘실을 불러오지 못했습니다.');
    } finally {
      if (contextRunRef.current === runId && contextLoadingRef.current) {
        contextLoadingRef.current = false;
        setLoading(false);
      }
    }
  }, [loadTimeline, seasonNo, userId]);

  const applyTimelineChange = useCallback((change) => {
    if (change?.source === 'messages-cleared') {
      historyRequestRef.current++;
      historyBusyRef.current = false;
      setHistory(null); setHistoryLoading(false); setHistoryError('');
      return true;
    }
    if (change?.source === 'message') {
      const id = text(change.rowId || change.row?.id);
      const removed = change.eventType === 'DELETE' || change.deleted;
      if (removed) historyRemovedRef.current.add(id);
      setHistory(current => {
        if (!current || !current.messages.some(message => message.id === id)) return current;
        return { ...current, messages: removed ? current.messages.filter(message => message.id !== id)
          : current.messages.map(message => message.id === id ? { ...message, ...change.row } : message) };
      });
    }
    // 재연결 스냅샷은 행 하나가 아니라 목록 전체다. rowId 로 거르면 여기서 떨어져 나가 뒤의
    // 전체 재조회로 넘어간다.
    if (change?.source === 'snapshot') {
      setTimeline((current) => {
        const messages = applyTimelineSnapshot(current.messages, change.messages, change.removedIds, true);
        if (messages === current.messages) return current;
        latestMessageAtRef.current = messages[messages.length - 1]?.createdAt || latestMessageAtRef.current;
        return { ...current, messages };
      });
      return true;
    }
    const rowId = text(change?.rowId || change?.row?.id);
    if (!rowId) return false;
    setTimeline((current) => {
      if (change.source === 'order') {
        const orders = change.eventType === 'DELETE'
          ? current.orders.filter((order) => order.id !== rowId)
          : upsertTimelineItem(current.orders, change.row);
        return { ...current, orders };
      }
      if (change.source === 'message') {
        const remove = change.eventType === 'DELETE' || change.deleted;
        const messages = remove
          ? current.messages.filter((message) => message.id !== rowId)
          : upsertTimelineItem(current.messages, change.row, true);
        latestMessageAtRef.current = messages[messages.length - 1]?.createdAt || latestMessageAtRef.current;
        return { ...current, messages };
      }
      return current;
    });
    return true;
  }, []);

  // scripts/test-verified-runtime-races.mjs 가 refresh 본문만 떼어 VM 에서 실행한다. 그래서 이 둘은
  // refresh 와 applyTimelineChange 사이가 아니라 그 뒤에 둔다.
  // timeline 은 '지시·실전 목록까지 다시 받아야 하는가'다. 모의전 보고는 채팅 메시지일 뿐이고
  // (워커가 모의전 기록을 건드리지 않는다) 멤버·모의전 변경도 지시·실전 줄을 바꾸지 않는다.
  // 보스 이름이 바뀌는 config 만 합성된 줄의 내용을 바꾸므로 그때만 목록까지 받는다.
  const scheduleSilentRefresh = useCallback(({ timeline = false } = {}) => {
    if (timeline) silentRefreshNeedsTimelineRef.current = true;
    if (silentRefreshTimerRef.current) return;
    silentRefreshTimerRef.current = window.setTimeout(() => {
      silentRefreshTimerRef.current = 0;
      const withTimeline = silentRefreshNeedsTimelineRef.current;
      silentRefreshNeedsTimelineRef.current = false;
      refresh({ silent: true, timeline: withTimeline });
    }, SILENT_REFRESH_DEBOUNCE_MS);
  }, [refresh]);

  // 도메인 알림이 몰아칠 때(운영진이 카드를 연달아 누르는 경우) 그 수만큼 목록을 받지 않는다.
  const scheduleTimelineReload = useCallback(() => {
    if (timelineReloadTimerRef.current) return;
    timelineReloadTimerRef.current = window.setTimeout(() => {
      timelineReloadTimerRef.current = 0;
      loadTimeline(contextRef.current).catch(() => {});
    }, TIMELINE_RELOAD_DEBOUNCE_MS);
  }, [loadTimeline]);

  // 보낸 글은 실시간 구독이 되돌려 주고, 그 길로 화면에 바로 붙는다. 그래도 조회를 한 번 더 걸던
  // 자리다 - 보낸 사람마다 지시·실전 목록까지 통째로 다시 받아 왔다. 이제는 되돌아오지 않았을 때만 건다.
  const scheduleSendEchoFallback = useCallback((nextContext, messageId) => {
    if (!messageId) return;
    const timer = window.setTimeout(() => {
      sendEchoTimersRef.current.delete(timer);
      if (timelineMessagesRef.current.some((message) => message.id === messageId)) return;
      loadTimeline(nextContext, { expectedMessageId: messageId }).catch(() => {});
    }, SEND_ECHO_GRACE_MS);
    sendEchoTimersRef.current.add(timer);
  }, [loadTimeline]);

  useEffect(() => () => {
    if (silentRefreshTimerRef.current) window.clearTimeout(silentRefreshTimerRef.current);
    if (timelineReloadTimerRef.current) window.clearTimeout(timelineReloadTimerRef.current);
    sendEchoTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    sendEchoTimersRef.current.clear();
  }, []);

  const recoverFromBackground = useCallback(() => {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    if (!context.unionId || !context.season?.id) return;
    const now = Date.now();
    if (now - lastResumeRefreshAtRef.current < RESUME_REFRESH_COOLDOWN_MS) return;
    lastResumeRefreshAtRef.current = now;
    // 구독을 다시 거는 것만으로는 모자랐다. 같은 store·같은 소켓을 그대로 다시 쓰기 때문에,
    // 자는 사이 죽은 소켓이면 끊긴 줄도 모른 채 옛 스냅샷을 받아 간다. 먼저 살아 있는지 묻는다.
    resyncRaidControlConnection({ unionId: context.unionId, seasonId: context.season.id });
    setSubscriptionEpoch((value) => value + 1);
    loadTimeline(context);
    if (seasonNo) overview.refreshLive(seasonNo);
  }, [context, loadTimeline, overview.refreshLive, seasonNo]);

  useEffect(() => { loadNikkeCatalog().then((result) => setCatalog(result.lookup)).catch(() => {}); }, []);
  useEffect(() => {
    refresh();
    return () => {
      contextRunRef.current += 1;
      timelineRunRef.current += 1;
      orderTimelineRunRef.current += 1;
      if (orderRefreshTimerRef.current) window.clearTimeout(orderRefreshTimerRef.current);
      orderRefreshTimerRef.current = 0;
      resultTimelineRunRef.current += 1;
      if (resultRefreshTimerRef.current) window.clearTimeout(resultRefreshTimerRef.current);
      resultRefreshTimerRef.current = 0;
    };
  }, [refresh]);
  useEffect(() => {
    if (!context.unionId || !context.season?.id) return undefined;
    return subscribeRaidControl({
      unionId: context.unionId,
      seasonId: context.season.id,
      onTimelineChange: (change) => {
        // 연결 상태는 타임라인 행이 아니다. 먼저 걸러 내지 않으면 아래 재조회까지 탄다.
        if (change?.source === 'realtime-status') { setRealtimeStatus(String(change.status || '')); return; }
        if (change?.source === 'presence') { setRoomPresence(Array.isArray(change.users) ? change.users : []); return; }
        if (change?.source === 'chat-header') { setChatHeader(change.header); return; }
        if (change?.source === 'read') return;
        if (change?.source === 'domain' && change.domain === 'orders') {
          if (!orderRefreshTimerRef.current) orderRefreshTimerRef.current = window.setTimeout(() => {
            orderRefreshTimerRef.current = 0;
            loadTimeline(context, { ordersOnly: true });
          }, 100);
          return;
        }
        if (change?.source === 'domain' && ['practice', 'roster', 'config'].includes(change.domain)) {
          // config 는 보스 이름을 바꾸고, 그 이름은 합성된 지시·실전 줄 안에 들어가 있다.
          scheduleSilentRefresh({ timeline: change.domain === 'config' });
          return;
        }
        const row = change?.row || {};
        const wrongUnion = row.unionId && row.unionId !== context.unionId;
        const wrongSeason = row.seasonId && row.seasonId !== context.season.id;
        if (wrongUnion || wrongSeason) return;
        if (seasonNo && ((change?.source === 'message' && row.type === 'raid_result') || (change?.source === 'domain' && change.domain === 'results') || change?.source === 'resync')) {
          overview.refreshLive(seasonNo);
        }
        if (change?.source === 'domain' && change.domain === 'results') {
          if (!resultRefreshTimerRef.current) resultRefreshTimerRef.current = window.setTimeout(() => {
            resultRefreshTimerRef.current = 0;
            loadTimeline(context, { resultsOnly: true });
          }, 100);
          return;
        }
        const changeHandled = applyTimelineChange(change);
        if (change?.source === 'message' && row.type === 'practice_report') {
          scheduleSilentRefresh();
          return;
        }
        if (changeHandled) return;
        // 공격 상태 변경은 바뀐 줄을 실어 보내지 않으므로(도메인 알림에는 unionId·seasonId·
        // 개정 번호뿐이다) 목록을 다시 받는 수밖에 없다. 다만 운영진이 카드 여러 장을 연달아
        // 누르면 그 수만큼 조회가 붙었다 - 짧게 묶어서 한 번으로 만든다.
        if (change?.source === 'domain') {
          scheduleTimelineReload();
          return;
        }
        loadTimeline(context, {
          expectedMessageId: change?.source === 'message' && change?.eventType === 'INSERT' ? change.rowId : '',
          expectedOrderId: change?.source === 'order' && change?.eventType === 'INSERT' ? change.rowId : '',
        });
      },
    });
  }, [applyTimelineChange, context, loadTimeline, overview.refreshLive, scheduleSilentRefresh, scheduleTimelineReload, seasonNo, subscriptionEpoch]);
  useEffect(() => {
    window.addEventListener('focus', recoverFromBackground);
    window.addEventListener('pageshow', recoverFromBackground);
    document.addEventListener('visibilitychange', recoverFromBackground);
    return () => {
      window.removeEventListener('focus', recoverFromBackground);
      window.removeEventListener('pageshow', recoverFromBackground);
      document.removeEventListener('visibilitychange', recoverFromBackground);
    };
  }, [recoverFromBackground]);

  const onlineUserIds = useMemo(() => new Set(roomPresence.map((member) => member.userId)), [roomPresence]);
  const presenceMembers = useMemo(() => onlineRaidMembers(context.members, roomPresence), [context.members, roomPresence]);
  const rosterMembers = useMemo(() => context.members
    .map((member) => ({ ...member, online: Boolean(member.userId && onlineUserIds.has(member.userId)) }))
    .sort((left, right) => Number(right.online) - Number(left.online) || left.nickname.localeCompare(right.nickname, 'ko-KR')), [context.members, onlineUserIds]);
  const overviewAligned = useMemo(() => {
    const sameUnion = !overview.unionId || !context.unionId || String(overview.unionId) === String(context.unionId);
    const overviewSeasonNo = Number(overview.selectedSeason?.seasonNo || 0);
    const contextSeasonNo = Number(context.season?.seasonNo || 0);
    return sameUnion && overviewSeasonNo > 0 && overviewSeasonNo === contextSeasonNo;
  }, [context.season?.seasonNo, context.unionId, overview.selectedSeason?.seasonNo, overview.unionId]);
  const bosses = useMemo(() => overviewAligned ? overview.selectedSeason?.bosses || [] : [], [overview.selectedSeason?.bosses, overviewAligned]);
  const actualSourceRecords = useMemo(() => overviewAligned ? overview.selectedSeason?.records || [] : [], [overview.selectedSeason?.records, overviewAligned]);
  // 현황이 같은 유니온·차수를 다 받아 뒀을 때만 그 기록을 쓴다. 로딩 중이거나 다른 차수를
  // 보고 있으면 관제 차수의 기존 조회를 그대로 둔다 - 사용자가 고른 현황 차수는 바꾸지 않는다.
  const reuseOverviewRecords = overviewAligned && !overview.loading && Array.isArray(overview.selectedSeason?.records);
  // 멤버 목록에 붙일 타수. 현황 기록을 못 쓰는 동안은 0타로 보이지 않게 집계 자체를 비워 둔다.
  const attackCountsKnown = reuseOverviewRecords;
  const attackCountByMember = useMemo(() => (attackCountsKnown
    ? buildRaidControlAttackCountByMember({ members: rosterMembers, records: actualSourceRecords })
    : new Map()), [actualSourceRecords, attackCountsKnown, rosterMembers]);
  const members = useMemo(() => rosterMembers.map((member) => ({
    ...member,
    attackLimit: RAID_CONTROL_ATTACK_LIMIT,
    attackCount: attackCountsKnown ? (attackCountByMember.get(member.memberOpenid) ?? 0) : null,
  })), [attackCountByMember, attackCountsKnown, rosterMembers]);
  // 접속 목록은 명단에 없는 사용자도 담는다 - 명단에 연결되지 않으면 타수를 매길 수 없다.
  const onlineMembers = useMemo(() => presenceMembers.map((member) => ({
    ...member,
    attackLimit: RAID_CONTROL_ATTACK_LIMIT,
    attackCount: attackCountsKnown && member.memberOpenid ? (attackCountByMember.get(member.memberOpenid) ?? 0) : null,
  })), [attackCountByMember, attackCountsKnown, presenceMembers]);
  useEffect(() => { reuseOverviewRecordsRef.current = reuseOverviewRecords; }, [reuseOverviewRecords]);
  const selectedMember = members.find((member) => member.memberOpenid === selectedMemberOpenid) || null;
  const canManageSelectedPractice = Boolean(selectedMember && (PRACTICE_MANAGER_ROLES.has(context.viewerRole) || selectedMember.userId === userId));
  const selectedMemberPracticeRecords = useMemo(() => context.practiceRecords.filter((record) => record.memberOpenid === selectedMemberOpenid), [context.practiceRecords, selectedMemberOpenid]);
  const availablePracticeRecordsByMember = useMemo(() => {
    const usedCodesByMember = new Map();
    actualSourceRecords.forEach((record) => {
      const memberOpenid = text(record.memberOpenid);
      if (!memberOpenid) return;
      const usedCodes = usedCodesByMember.get(memberOpenid) || new Set();
      raidControlDeckCodes(record.deck, catalog).forEach((code) => usedCodes.add(code));
      usedCodesByMember.set(memberOpenid, usedCodes);
    });

    const availableByMember = new Map();
    context.practiceRecords.forEach((record) => {
      const memberOpenid = text(record.memberOpenid);
      if (!memberOpenid || record.used) return;
      const deck = raidControlDeckCodes(record.deck, catalog);
      const usedCodes = usedCodesByMember.get(memberOpenid);
      if (deck.some((code) => usedCodes?.has(code))) return;
      const memberRecords = availableByMember.get(memberOpenid) || [];
      memberRecords.push({ ...record, deck });
      availableByMember.set(memberOpenid, memberRecords);
    });
    availableByMember.forEach((records) => records.sort((left, right) => Number(left.bossNo) - Number(right.bossNo) || Number(left.slotNo) - Number(right.slotNo)));
    return availableByMember;
  }, [actualSourceRecords, catalog, context.practiceRecords]);
  const selectedPracticeRecords = useMemo(() => availablePracticeRecordsByMember.get(selectedMemberOpenid) || [], [availablePracticeRecordsByMember, selectedMemberOpenid]);
  const hiddenPracticeCountByBoss = useMemo(() => {
    const counts = new Map();
    selectedMemberPracticeRecords.forEach((record) => {
      if (!selectedPracticeRecords.some((visible) => visible.id === record.id)) counts.set(record.bossNo, (counts.get(record.bossNo) || 0) + 1);
    });
    return counts;
  }, [selectedMemberPracticeRecords, selectedPracticeRecords]);

  const createTetrisPreview = useCallback((bossNo = selectedBossNo, options = {}) => {
    if (!overviewAligned) return { share: null, error: '레이드 현황과 관제 차수가 일치한 뒤 다시 시도해주세요.' };
    const boss = bosses.find((item) => Number(item.bossNo) === Number(bossNo)) || null;
    return buildRaidControlTetrisPreview({
      members,
      actualRecords: actualSourceRecords,
      practiceRecords: context.practiceRecords,
      bosses,
      boss,
      seasonNo: context.season?.seasonNo || seasonNo || 0,
      mode: options.mode || 'nearest',
      preferAvailability: Boolean(options.preferAvailability),
      currentAvailabilityOnly: Boolean(options.currentAvailabilityOnly),
      excludedMemberOpenids: options.excludedMemberOpenids || [],
      catalog,
    });
  }, [actualSourceRecords, bosses, catalog, context.practiceRecords, context.season?.seasonNo, members, overviewAligned, seasonNo, selectedBossNo]);

  const matchedTimeline = useMemo(() => {
    const actualByRecordKey = new Map(actualSourceRecords
      .map((record) => [String(record.recordKey || record.id || ''), record])
      .filter(([recordKey]) => recordKey));
    const bossNameByNo = new Map(bosses.map((boss) => [Number(boss.bossNo), String(boss.bossName || '보스')]));
    const ordersById = new Map(timeline.orders.map((order) => {
      const displayBossNo = Number(order.bossNo);
      return [order.id, {
        ...order,
        bossName: bossNameByNo.get(displayBossNo) || '',
        deck: raidControlDeckCodes(order.deck, catalog),
      }];
    }));
    const currentNicknameByUserId = new Map(
      (Array.isArray(context.members) ? context.members : [])
        .map((member) => [String(member?.userId || ''), String(member?.nickname || '').trim()])
        .filter(([memberUserId, nickname]) => memberUserId && nickname),
    );
    const buildMessage = (message) => {
      const nikkeRefs = resolveMessageNikkeRefs(message.nikkeRefs, catalog);
      const currentAuthorNickname = currentNicknameByUserId.get(String(message.authorUserId || '')) || '';
      const reconciledMessage = currentAuthorNickname && currentAuthorNickname !== message.authorNickname
        ? { ...message, authorNickname: currentAuthorNickname }
        : message;
      if (reconciledMessage.type !== 'raid_result') return { ...reconciledMessage, nikkeRefs };
      if ((reconciledMessage.unionId && reconciledMessage.unionId !== context.unionId)
        || (reconciledMessage.seasonId && reconciledMessage.seasonId !== context.season?.id)) {
        return { ...reconciledMessage, nikkeRefs };
      }
      const recordKey = String(reconciledMessage.eventData?.recordKey || reconciledMessage.sourceRecordKey || '');
      const actual = actualByRecordKey.get(recordKey);
      if (!actual) {
        return {
          ...reconciledMessage,
          nikkeRefs,
          eventData: {
            ...reconciledMessage.eventData,
            deck: raidControlDeckCodes(reconciledMessage.eventData?.deck, catalog),
          },
        };
      }
      const sourceOrderKnown = Boolean(actual.attackedAt || actual.attacked_at);
      return {
        ...reconciledMessage,
        nikkeRefs,
        eventData: {
          ...reconciledMessage.eventData,
          recordKey,
          memberOpenid: actual.memberOpenid,
          memberNickname: actual.nickname,
          bossNo: actual.bossNo,
          bossName: bossNameByNo.get(Number(actual.bossNo)) || actual.bossName || reconciledMessage.eventData?.bossName || '보스',
          phaseNo: actual.phaseNo,
          attackNo: sourceOrderKnown ? actual.attackNo : null,
          damageEok: actual.damageEok,
          isKill: actual.isKill,
          attackedAt: actual.attackedAt,
          sourceOrderKnown,
          identityMethod: actual.identityMethod || actual.identity_method || reconciledMessage.eventData?.identityMethod || '',
          deck: raidControlDeckCodes(actual.deck, catalog),
        },
      };
    };
    // 들어온 메시지가 그대로면 지난번에 만든 객체를 그대로 돌려준다. 새로 펼쳐 담으면 메시지 하나가
    // 도착할 때마다 화면에 올라와 있는 160개 말풍선의 신원이 전부 바뀌어 다시 그려진다.
    // 재료(도감·보스·실전 기록·시즌)가 바뀌면 지난 결과는 못 쓰니 캐시를 버린다.
    const mappingInputs = [actualSourceRecords, bosses, catalog, context.members, context.season?.id, context.unionId, reuseOverviewRecords];
    const cache = mappedMessagesRef.current;
    const reusable = cache.inputs.length === mappingInputs.length
      && cache.inputs.every((input, index) => input === mappingInputs[index]);
    const previousByMessage = reusable ? cache.byMessage : new Map();
    const byMessage = new Map();
    // 타임라인이 실전 기록을 안 받아 왔으면(현황이 이미 들고 있으니) 그 기록으로 줄을 만든다.
    // 받아 온 경우에는 같은 record_key 가 이미 있으므로 아무것도 더해지지 않는다.
    const seenRecordKeys = new Set(visibleTimelineMessages
      .filter((message) => message.type === 'raid_result')
      .map((message) => String(message.sourceRecordKey || message.eventData?.recordKey || '')));
    const runtimeForSynthetic = { unionId: context.unionId, seasonId: context.season?.id };
    // 합쳐 놓고 다시 시간순으로 세운다. 뒤에 붙이기만 하면 목록의 마지막이 최신이 아니게 되는데,
    // 읽음 표시가 마지막 원소의 시각을 쓴다 - 옛 시각으로 읽음이 찍혀 배지가 빨간 채로 남았다.
    // 새로 만든 배열만 정렬한다(visibleTimelineMessages 는 다른 곳과 공유한다).
    const sourceMessages = reuseOverviewRecords
      ? sortTimelineItems([...visibleTimelineMessages, ...actualSourceRecords
        .filter((record) => !seenRecordKeys.has(String(record?.recordKey ?? record?.record_key ?? '')))
        .map((record) => syntheticRaidResultMessage(record, runtimeForSynthetic, bossNameByNo))
        .filter(Boolean)], true)
      : visibleTimelineMessages;
    const messages = sourceMessages.map((message) => {
      const mapped = previousByMessage.get(message) || buildMessage(message);
      byMessage.set(message, mapped);
      return mapped;
    }).filter((message) => raidControlTimelineMessageVisible(message, ordersById));
    mappedMessagesRef.current = { inputs: mappingInputs, byMessage };
    return { ordersById, messages };
  }, [actualSourceRecords, bosses, catalog, context.members, context.season?.id, context.unionId, reuseOverviewRecords, visibleTimelineMessages, timeline.orders]);
  const ordersById = matchedTimeline.ordersById;
  useEffect(() => { timelineMessagesRef.current = timeline.messages; }, [timeline.messages]);
  useEffect(() => { contextRef.current = context; }, [context]);
  const changeChatHeader = useCallback(async (action, value) => {
    if (!MANAGER_ROLES.has(context.viewerRole) || chatHeaderBusyRef.current) return false;
    const scope = historyScopeRef.current;
    chatHeaderBusyRef.current = true; setChatHeaderWorking(true); setChatHeaderError('');
    try {
      const header = await updateRaidControlChatHeader({ unionId: context.unionId, seasonId: context.season?.id,
        action, ...(action === 'notice' ? { body: value } : { messageId: value }) });
      if (scope === historyScopeRef.current) setChatHeader(header);
      return true;
    } catch (error) {
      if (scope === historyScopeRef.current) setChatHeaderError(error.message || '공지·고정을 변경하지 못했습니다.');
      return false;
    } finally { chatHeaderBusyRef.current = false; setChatHeaderWorking(false); }
  }, [context]);

  const attackingByBoss = useMemo(() => {
    const grouped = new Map();
    for (const order of ordersById.values()) {
      if (order.status !== 'attacking') continue;
      const attacks = grouped.get(order.bossNo) || [];
      attacks.push(order);
      grouped.set(order.bossNo, attacks);
    }
    return grouped;
  }, [ordersById]);

  const sendMessage = useCallback(async ({ body, stickerId = null, stickerIds = null, mentionedUserIds = [], nikkeRefs = [], replyToId = null }) => {
    if (working || !context.season?.id) return false;
    if (hasDuplicateRepracticeRequest(timeline.messages, nikkeRefs)) return true;
    setWorking('message');
    try {
      const messageId = await sendRaidControlMessage({ seasonId: context.season.id, body, stickerId, stickerIds, mentionedUserIds, nikkeRefs, replyToId });
      // 서버가 받았다는 응답(ACK)이 오면 보낸 것이다. 여기서 끝낸다.
      // 전에는 아래 타임라인 갱신까지 기다린 뒤에야 돌려줬다. 그 사이 버튼이 잠겨 있는 것은
      // 물론이고 입력칸의 글도 지워지지 않아, 보냈는데 멈춘 것처럼 보였다. 갱신은 기대한
      // 글이 보일 때까지 재시도한다(0·80·180·360ms) - 거기에 조회 왕복까지 더해진다.
      // 갱신 자체는 필요하지만 기다릴 일이 아니다. 실시간 구독이 이미 같은 글을 밀어 준다.
      setWorking('');
      showLatestMessages();
      scheduleChatScrollToLatest();
      // 알림은 멘션 받은 사람에게만 간다. 멘션이 없으면 보낼 곳이 없는데도 매번 엣지 함수를
      // 부르고, 그게 워커를 거쳐 방을 깨우고, 받는 사람 없는 발송 기록까지 한 줄 남겼다.
      // 측정(2026-10-10, 한 유니온 하루): 메시지 4,853개 중 4,802개가 멘션 없음, 그중
      // 4,798개가 그 기록을 남겼다. @전체는 보낼 때 이미 사람 목록으로 펼쳐져 들어온다.
      if (mentionedUserIds.length) {
        requestRaidControlPush(messageId, { seasonNo: context.season.seasonNo }).catch(() => {});
      }
      scheduleSendEchoFallback(context, messageId);
      return true;
    } catch (error) { setErrorMessage(error instanceof Error ? error.message : '메시지를 보내지 못했습니다.'); return false; }
    finally { setWorking(''); }
  }, [context, scheduleSendEchoFallback, showLatestMessages, timeline.messages, working]);

  const sendPracticeReport = useCallback(async (payload) => {
    if (working || !context.season?.id) return false;
    setWorking('practice');
    try {
      const messageId = await sendRaidControlPracticeReport({ ...payload, seasonId: context.season.id });
      setWorking('');
      scheduleChatScrollToLatest();
      scheduleSendEchoFallback(context, messageId);
      return true;
    } catch (error) { setErrorMessage(error instanceof Error ? error.message : '모의전 결과를 공유하지 못했습니다.'); return false; }
    finally { setWorking(''); }
  }, [context, scheduleSendEchoFallback, showLatestMessages, working]);

  const createPractice = useCallback(async ({ targetMemberOpenid, bossNo, payload }) => {
    if (working || !context.season?.seasonNo || !payload) return false;
    const targetMember = context.members.find((member) => text(member.memberOpenid) === text(targetMemberOpenid)) || null;
    const canManageAll = PRACTICE_MANAGER_ROLES.has(context.viewerRole);
    const canManageTarget = canManageAll || Boolean(targetMember?.userId && targetMember.userId === userId);
    if (!targetMember || !canManageTarget) {
      setPracticeNotice({ type: 'error', message: '일반 멤버는 자신의 모의전 기록만 추가할 수 있습니다.' });
      return false;
    }

    const slotNo = Number(payload.slotNo || 0);
    setWorking(`save:${slotNo}`);
    setPracticeNotice({ type: '', message: '' });
    try {
      const result = canManageAll
        ? await createRaidPracticeAdmin({
          seasonNo: context.season.seasonNo,
          memberOpenid: targetMember.memberOpenid,
          bossNo,
          payload,
        })
        : await saveRaidPractice({
          ...payload,
          seasonNo: context.season.seasonNo,
          bossNo,
        });
      const nextRecord = normalizePracticeMutation(result, {
        memberOpenid: targetMember.memberOpenid,
        memberNickname: targetMember.nickname,
        bossNo,
        slotNo,
        title: payload.title,
        damageEok: payload.damageEok,
        deck: payload.deck,
        used: payload.used,
        note: '',
      });
      setContext((current) => ({ ...current, practiceRecords: upsertPracticeRecord(current.practiceRecords, nextRecord) }));
      setErrorMessage('');
      setPracticeNotice({ type: 'success', message: `${targetMember.nickname || '멤버'}의 ${slotNo}번 모의전 슬롯을 추가했습니다.` });
      return true;
    } catch (error) {
      setPracticeNotice({ type: 'error', message: error instanceof Error ? error.message : '모의전 기록을 추가하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [context, userId, working]);

  const updatePractice = useCallback(async ({ record, targetMemberOpenid, bossNo, payload }) => {
    if (working || !context.season?.seasonNo || !record?.id || !payload) return false;
    const targetMember = context.members.find((member) => text(member.memberOpenid) === text(targetMemberOpenid)) || null;
    const canManageAll = PRACTICE_MANAGER_ROLES.has(context.viewerRole);
    const canManageTarget = canManageAll || Boolean(targetMember?.userId && targetMember.userId === userId);
    if (!targetMember || !canManageTarget || text(record.memberOpenid) !== text(targetMember.memberOpenid)) {
      setPracticeNotice({ type: 'error', message: '일반 멤버는 자신의 모의전 기록만 수정할 수 있습니다.' });
      return false;
    }

    const slotNo = Number(record.slotNo || payload.slotNo || 0);
    setWorking(`save:${slotNo}`);
    setPracticeNotice({ type: '', message: '' });
    try {
      const result = canManageAll
        ? await updateRaidPracticeAdmin({ recordId: record.id, payload: { ...payload, slotNo } })
        : await saveRaidPractice({ ...payload, slotNo, seasonNo: context.season.seasonNo, bossNo });
      const nextRecord = normalizePracticeMutation(result, {
        ...record,
        id: record.id,
        memberOpenid: targetMember.memberOpenid,
        memberNickname: targetMember.nickname,
        bossNo,
        slotNo,
        title: payload.title,
        damageEok: payload.damageEok,
        deck: payload.deck,
        used: payload.used,
      });
      setContext((current) => ({ ...current, practiceRecords: upsertPracticeRecord(current.practiceRecords, nextRecord) }));
      setErrorMessage('');
      setPracticeNotice({ type: 'success', message: `${targetMember.nickname || '멤버'}의 ${slotNo}번 모의전 슬롯을 수정했습니다.` });
      return true;
    } catch (error) {
      setPracticeNotice({ type: 'error', message: error instanceof Error ? error.message : '모의전 기록을 수정하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [context, userId, working]);

  const deletePracticeRecord = useCallback(async ({ record, targetMemberOpenid }) => {
    if (working || !record?.id) return false;
    const targetMember = context.members.find((member) => text(member.memberOpenid) === text(targetMemberOpenid)) || null;
    const canManageAll = PRACTICE_MANAGER_ROLES.has(context.viewerRole);
    const canManageTarget = canManageAll || Boolean(targetMember?.userId && targetMember.userId === userId);
    if (!targetMember || !canManageTarget || text(record.memberOpenid) !== text(targetMember.memberOpenid)) {
      setPracticeNotice({ type: 'error', message: '일반 멤버는 자신의 모의전 기록만 삭제할 수 있습니다.' });
      return false;
    }

    const slotNo = Number(record.slotNo || 0);
    setWorking(`delete:${slotNo}`);
    setPracticeNotice({ type: '', message: '' });
    try {
      const deleted = canManageAll
        ? await deleteRaidPracticeAdmin({ recordId: record.id })
        : await deleteRaidPractice(record.id);
      if (!deleted) throw new Error('모의전 기록을 삭제하지 못했습니다.');
      setContext((current) => ({
        ...current,
        practiceRecords: current.practiceRecords.filter((item) => text(item.id) !== text(record.id)),
      }));
      setErrorMessage('');
      setPracticeNotice({ type: 'success', message: `${targetMember.nickname || '멤버'}의 ${slotNo}번 모의전 슬롯을 삭제했습니다.` });
      return true;
    } catch (error) {
      setPracticeNotice({ type: 'error', message: error instanceof Error ? error.message : '모의전 기록을 삭제하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [context, userId, working]);

  const createOrder = useCallback(async (payload) => {
    if (working || orderMutationRef.current || !context.season?.id) return false;
    orderMutationRef.current = true;
    try {
      const base = { ...payload, seasonId: context.season.id };
      const result = payload.customDeck ? await createRaidControlCustomOrder({ ...base, deck: payload.customDeck }) : await createRaidControlOrder(base);
      const now = new Date().toISOString();
      const targetMember = context.members.find((member) => member.memberOpenid === payload.targetMemberOpenid) || null;
      const authorMember = context.members.find((member) => member.userId === userId) || null;
      const practice = context.practiceRecords.find((record) => record.id === payload.practiceRecordId) || null;
      const bossName = bosses.find((boss) => Number(boss.bossNo) === Number(payload.bossNo))?.bossName || '보스';
      const deck = payload.customDeck ? payload.customDeck : practice?.deck || [];
      const optimisticOrder = {
        id: result.orderId,
        unionId: context.unionId,
        seasonId: context.season.id,
        targetMemberOpenid: text(payload.targetMemberOpenid),
        targetUserId: result.targetUserId,
        targetNickname: targetMember?.nickname || practice?.memberNickname || '멤버',
        bossNo: Number(payload.bossNo),
        phaseNo: Number(payload.phaseNo),
        practiceRecordId: text(payload.practiceRecordId),
        practiceTitle: text(payload.customDeck ? payload.title : practice?.title),
        expectedDamageEok: payload.customDeck ? Number(payload.expectedDamageEok || 0) : Number(practice?.damageEok || 0),
        deck,
        note: text(payload.note),
        status: 'sent',
        createdBy: userId,
        acknowledgedAt: '',
        startedAt: '',
        completedAt: '',
        baselineCapturedAt: '',
        actualRecordKey: '',
        actualEventData: null,
        actualLinkedAt: '',
        createdAt: now,
        updatedAt: now,
      };
      const optimisticMessage = {
        id: result.messageId,
        unionId: context.unionId,
        seasonId: context.season.id,
        authorUserId: userId,
        authorNickname: authorMember?.nickname || '운영진',
        type: 'attack_order',
        body: `${optimisticOrder.targetNickname} · ${bossName} ${optimisticOrder.phaseNo}단 공격 지시`,
        orderId: result.orderId,
        replyToId: '',
        mentionedUserIds: result.targetUserId ? [result.targetUserId] : [],
        nikkeRefs: [],
        sourceRecordKey: '',
        eventData: {},
        createdAt: now,
      };
      setTimeline((current) => ({
        ...current,
        orders: upsertTimelineItem(current.orders, optimisticOrder),
        messages: upsertTimelineItem(current.messages, optimisticMessage, true),
      }));
      setErrorMessage('');
      await loadTimeline(context, { expectedMessageId: result.messageId, expectedOrderId: result.orderId, ordersOnly: true });
      requestRaidControlPush(result.messageId).catch(() => {});
      return true;
    } catch (error) { setErrorMessage(error instanceof Error ? error.message : '공격 지시를 보내지 못했습니다.'); return false; }
    finally { orderMutationRef.current = false; }
  }, [bosses, context, loadTimeline, userId, working]);

  const dismissAttackConfirmation = useCallback((orderId) => {
    setPendingAttackConfirmations(current => { const next = { ...current }; delete next[orderId]; return next; });
    setOrderErrors(current => { const next = { ...current }; delete next[orderId]; return next; });
  }, []);
  const changeOrderStatus = useCallback(async (orderId, status, { confirmConcurrent = false } = {}) => {
    if (working || orderMutationRef.current) return false;
    const order = timeline.orders.find(item => item.id === orderId);
    const memberAttack = timeline.orders.find(item => item.id !== orderId && item.status === 'attacking'
      && item.targetMemberOpenid === order?.targetMemberOpenid);
    if (status === 'attacking' && memberAttack) {
      dismissAttackConfirmation(orderId);
      setOrderErrors(current => ({ ...current, [orderId]: `${order.targetNickname || '이 멤버'} 님은 이미 ${memberAttack.bossNo}번 보스 ${memberAttack.phaseNo}단계를 공격 중입니다. 기존 공격을 완료하거나 상태 해제 후 시작해주세요.` }));
      return false;
    }
    setOrderErrors(current => { const next = { ...current }; delete next[orderId]; return next; });
    const others = timeline.orders.filter(item => item.id !== orderId && item.status === 'attacking'
      && item.bossNo === order?.bossNo && item.targetMemberOpenid !== order?.targetMemberOpenid);
    if (status === 'attacking' && !confirmConcurrent && others.length) {
      setPendingAttackConfirmations(current => current[orderId] ? current : {
        ...current, [orderId]: { names: [...new Set(others.map(item => item.targetNickname).filter(Boolean))].join(', ') },
      });
      return false;
    }
    orderMutationRef.current = true;
    try {
      const updatedOrder = await updateRaidControlOrderStatus(orderId, status, { confirmConcurrent });
      if (updatedOrder.id) {
        setTimeline((current) => ({ ...current, orders: upsertTimelineItem(current.orders, updatedOrder) }));
      } else {
        await loadTimeline(context, { expectedOrderId: orderId, ordersOnly: true });
      }
      setPendingAttackConfirmations(current => { const next = { ...current }; delete next[orderId]; return next; });
      setErrorMessage('');
      return true;
    } catch (error) {
      if (status === 'attacking' && error?.code === 'boss_attack_confirmation_required') {
        setPendingAttackConfirmations(current => current[orderId] ? current : { ...current, [orderId]: { names: '' } });
        return false;
      }
      if (error?.code === 'member_order_already_in_progress') dismissAttackConfirmation(orderId);
      setOrderErrors(current => ({ ...current, [orderId]: error instanceof Error ? error.message : '공격 상태를 변경하지 못했습니다.' }));
      return false;
    }
    finally { orderMutationRef.current = false; }
  }, [context, dismissAttackConfirmation, loadTimeline, timeline.orders, working]);

  // 읽음 표시는 접속자 한 명이 새 메시지를 볼 때마다 한 번씩 올라간다. 접속자 x 메시지로 늘어나서
  // 측정해 보니 Durable Object 에 쓰는 행의 80~94%가 이것이었고(30명 x 1,500메시지면 하루 한도
  // 10만 행의 98%), 한도를 넘기면 그날 남은 시간 동안 채팅이 아예 실패한다. 사용자 눈에 보이는 건
  // 안 읽음 배지뿐이고 그건 아래에서 바로 지우므로, 서버 기록만 모아서 보낸다.
  const flushReadMark = useCallback(async () => {
    const state = readMarkRef.current;
    if (state.timer) { clearTimeout(state.timer); state.timer = null; }
    const at = state.pendingAt;
    if (!at || at === state.sentAt || !context.unionId || !userId) return false;
    state.sentAt = at;
    try {
      await markRaidControlRead({ unionId: context.unionId, userId, at });
      return true;
    } catch {
      state.sentAt = '';
      return false;
    }
  }, [context.unionId, userId]);

  const markReadThrough = useCallback(async (at = latestMessageAtRef.current) => {
    if (!context.unionId || !userId || !at) return false;
    const state = readMarkRef.current;
    // ISO 8601 UTC 문자열은 사전순 비교가 곧 시간순 비교다.
    if (at > state.pendingAt) state.pendingAt = at;
    setUnread({ anchorId: '', count: 0, lastReadAt: state.pendingAt });
    // 읽은 지점을 같이 실어 보낸다. 서버 쓰기는 떠날 때나 10분 뒤라, 받는 쪽이 그때 서버를
    // 다시 읽으면 아직 옛 값이다 - 다 읽고 나왔는데도 배지가 빨간 채로 남았다.
    window.dispatchEvent(new CustomEvent('doroparty:raid-control-read', { detail: { at: state.pendingAt } }));
    if (!state.timer) state.timer = setTimeout(() => { flushReadMark(); }, READ_MARK_FLUSH_MS);
    return true;
  }, [context.unionId, userId, flushReadMark]);

  // 읽음 지점이 실제로 필요한 순간은 '다시 들어올 때' 하나뿐이므로, 떠나는 길목에서만 올린다.
  // pagehide 는 폰에서 탭이 bfcache 로 들어갈 때, visibilitychange 는 앱 전환·화면 잠금에서 뜬다.
  useEffect(() => {
    const flushWhenHidden = () => { if (document.visibilityState === 'hidden') flushReadMark(); };
    document.addEventListener('visibilitychange', flushWhenHidden);
    window.addEventListener('pagehide', flushReadMark);
    return () => {
      document.removeEventListener('visibilitychange', flushWhenHidden);
      window.removeEventListener('pagehide', flushReadMark);
      flushReadMark();
    };
  }, [flushReadMark]);

  return {
    overview, bosses, context, catalog, members, onlineMembers, presenceStatus: realtimeStatus === 'open' ? 'open' : 'connecting', presenceError: '', selectedMember, selectedMemberOpenid, selectedPracticeRecords,
    availablePracticeRecordsByMember, hiddenPracticeCountByBoss, selectedBossNo, messages: matchedTimeline.messages, ordersById, attackingByBoss,
    unreadAnchorId: unread.anchorId, unreadCount: unread.count,
    canDirect: MANAGER_ROLES.has(context.viewerRole), canManageSelectedPractice, loading, working, errorMessage, practiceNotice,
    realtimeStatus,
    chatHeader, chatHeaderWorking, chatHeaderError, changeChatHeader,
    historyMode: Boolean(history), historyPageVersion: history?.pageVersion || 0, historyLoading, historyError, hasOlderMessages: history ? history.hasMore : timeline.messages.some(message => !['attack_order', 'raid_result'].includes(message.type)),
    loadOlderMessages, showLatestMessages, pendingAttackConfirmations, dismissAttackConfirmation, orderErrors,
    selectMember: setSelectedMemberOpenid, selectBoss: setSelectedBossNo, sendMessage, sendPracticeReport,
    createPractice, updatePractice, deletePractice: deletePracticeRecord, createOrder,
    createTetrisPreview, changeOrderStatus, markReadThrough, refresh,
  };
}
